import { useEffect, useState } from 'react';
import { generacionApi, type ContenidoItem } from '../lib/api';

const TIPO_LABEL: Record<string, string> = {
  texto: 'Post', carrusel: 'Carrusel', historia: 'Historia',
  historia_cta: 'Historia CTA', reel: 'Reel', pdf: 'PDF',
};

const TIPO_TONE: Record<string, { soft: string; ink: string; solid: string; line: string }> = {
  texto:       { soft: 'var(--copper-soft)', ink: 'var(--copper-ink)', solid: 'var(--copper)', line: 'var(--copper-line)' },
  carrusel:    { soft: 'var(--sage-soft)',   ink: 'var(--sage-ink)',   solid: 'var(--sage)',   line: 'var(--sage-line)'   },
  reel:        { soft: 'var(--clay-soft)',   ink: 'var(--clay)',       solid: 'var(--clay)',   line: '#e7c9c1'            },
  historia:    { soft: 'var(--amber-soft)',  ink: 'var(--amber)',      solid: 'var(--amber)',  line: '#e7d7ab'            },
  historia_cta:{ soft: 'var(--amber-soft)',  ink: 'var(--amber)',      solid: 'var(--amber)',  line: '#e7d7ab'            },
  pdf:         { soft: 'var(--info-soft)',   ink: 'var(--info)',       solid: 'var(--info)',   line: '#cbd9e2'            },
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getDate()} ${['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()]} ${d.getFullYear()}`;
}
function fmt(n: number) { return n.toLocaleString('es-MX'); }

export default function Historial() {
  const [items, setItems]     = useState<ContenidoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [tipoFilter, setTipoFilter] = useState('all');

  useEffect(() => {
    generacionApi.getHistorial()
      .then(r => setItems(r.items))
      .catch(e => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
  }, []);

  const tipos = [...new Set(items.map(i => i.tipo))];
  const list  = tipoFilter === 'all' ? items : items.filter(i => i.tipo === tipoFilter);

  const thisMonth = items.filter(i => {
    if (!i.published_at) return false;
    const d = new Date(i.published_at); const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  return (
    <div className="page page-wide">
      <h1 style={{ fontFamily: 'var(--serif)', fontSize: 28, margin: 0 }}>Historial</h1>
      <p className="muted" style={{ margin: '7px 0 22px', fontSize: 14 }}>
        Registro de todo el contenido publicado, con su fecha de creación y de publicación.
      </p>

      {/* Stats */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 'var(--gap)' }}>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--info-soft)', color: 'var(--info)' }}><EyeIco /></span>
          </div>
          <div className="stat-num tabular">{items.length}</div>
          <div className="stat-label">Publicaciones totales</div>
          <div className="stat-delta flat">historial completo</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--copper-soft)', color: 'var(--copper)' }}><MegaIco /></span>
          </div>
          <div className="stat-num tabular">{fmt(thisMonth.length)}</div>
          <div className="stat-label">Publicados este mes</div>
          <div className="stat-delta up"><ArrowUpIco size={13} /> este mes</div>
        </div>
        <div className="card stat fade-up">
          <div className="stat-top">
            <span className="stat-ico" style={{ background: 'var(--sage-soft)', color: 'var(--sage)' }}><BookmarkIco /></span>
          </div>
          <div className="stat-num tabular">{new Set(items.map(i => i.tipo)).size}</div>
          <div className="stat-label">Formatos distintos</div>
          <div className="stat-delta flat">variedad de piezas</div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="row card-pad" style={{ justifyContent: 'space-between', paddingBottom: 14 }}>
          <div className="row" style={{ gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Publicaciones</h3>
            <span className="tag">{list.length}</span>
          </div>
          <div className="row" style={{ gap: 7, flexWrap: 'wrap' }}>
            <button onClick={() => setTipoFilter('all')} className="tag" style={{ cursor: 'pointer', height: 28,
              border: tipoFilter === 'all' ? '1px solid var(--accent)' : '1px solid var(--border)',
              background: tipoFilter === 'all' ? 'var(--accent-soft)' : 'var(--surface-2)',
              color: tipoFilter === 'all' ? 'var(--accent-ink)' : 'var(--ink-2)' }}>
              Todos
            </button>
            {tipos.map(tip => (
              <button key={tip} onClick={() => setTipoFilter(tip === tipoFilter ? 'all' : tip)} className="tag"
                style={{ cursor: 'pointer', height: 28,
                  border: tipoFilter === tip ? '1px solid var(--accent)' : '1px solid var(--border)',
                  background: tipoFilter === tip ? 'var(--accent-soft)' : 'var(--surface-2)',
                  color: tipoFilter === tip ? 'var(--accent-ink)' : 'var(--ink-2)' }}>
                {TIPO_LABEL[tip] ?? tip}
              </button>
            ))}
          </div>
        </div>
        <hr className="hr" />

        {loading && <div className="loading-center"><span className="spinner" /> Cargando historial…</div>}

        {!loading && list.length === 0 && (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--muted)' }}>
            <span style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: 'var(--ink-2)' }}><MegaIco size={24} /></span>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 17, color: 'var(--ink)' }}>Sin publicaciones</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>{tipoFilter !== 'all' ? 'No hay publicaciones con este filtro' : 'Aún no se ha publicado ninguna pieza'}</div>
          </div>
        )}

        {!loading && list.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Contenido</th>
                <th>Formato</th>
                <th>Creado</th>
                <th>Publicado</th>
                <th style={{ textAlign: 'right' }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {list.map(item => {
                const t = TIPO_TONE[item.tipo] ?? TIPO_TONE.texto;
                return (
                  <tr key={item.id}>
                    <td style={{ maxWidth: 360 }}>
                      <span style={{ fontWeight: 600 }}>{item.titulo}</span>
                    </td>
                    <td>
                      <span className="tag" style={{ background: t.soft, color: t.ink, borderColor: t.line, height: 21, fontSize: 11 }}>
                        <span className="dot" style={{ background: t.solid }} />
                        {TIPO_LABEL[item.tipo] ?? item.tipo}
                      </span>
                    </td>
                    <td className="muted">{fmtDate(item.created_at)}</td>
                    <td>
                      <span className="row" style={{ gap: 7 }}>
                        <span style={{ color: 'var(--sage)' }}><MegaIco size={14} /></span>
                        {fmtDate(item.published_at)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="chip chip-publicado"><span className="dot" /> Publicado</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── Icons ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function EyeIco({ size = 18 }: { size?: number })      { return <Svg size={size}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></Svg>; }
function MegaIco({ size = 18 }: { size?: number })     { return <Svg size={size}><path d="M3 11v2a1 1 0 0 0 1 1h2l8 5V5L6 10H4a1 1 0 0 0-1 1z"/><path d="M18 8a4 4 0 0 1 0 8"/></Svg>; }
function BookmarkIco({ size = 18 }: { size?: number }) { return <Svg size={size}><path d="M6 4h12v17l-6-4-6 4z"/></Svg>; }
function ArrowUpIco({ size = 18 }: { size?: number })  { return <Svg size={size}><path d="M12 19V5M6 11l6-6 6 6"/></Svg>; }
